import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, map } from 'rxjs';
import { firstValueFromAbortable } from '@mb/common/client';
import type {
  AssignPersonPositionInput,
  CreateOrganizationUnitInput,
  CreatePositionInput,
  CreateStationInput,
  MovePersonPositionResult,
  OrganizationUnitDto,
  OrgTreeResponse,
  PositionDto,
  StationDto,
  UpdateOrganizationUnitInput,
  UpdatePositionInput,
  UpdateStationInput,
} from '../../shared';
import type {
  RoleWithScopedPermissionsDto,
  IScopedEntity,
} from '@mb/auth/shared';
import { PersonSearchDto } from '@modules/contacts/shared';
import { ApiResponse } from '@mb/common/shared';

/** Minimal role shape for the position role editor. */
export interface OrgRoleOption {
  id: string;
  name: string;
  description: string;
}

@Injectable({ providedIn: 'root' })
export class OrgChartApiService {
  readonly #http = inject(HttpClient);
  readonly #base = '/api/v1/organization';

  getTree(
    organizationId?: string,
    options?: { signal?: AbortSignal },
  ): Promise<OrgTreeResponse> {
    const params = organizationId ? { organizationId } : undefined;
    return firstValueFromAbortable(
      this.#http.get<OrgTreeResponse>(`${this.#base}/tree`, { params }),
      options?.signal,
    );
  }

  /** Drag-and-drop reassignment. */
  movePersonPosition(
    personPositionId: string,
    toPositionId: string,
  ): Promise<{ success: boolean; data: MovePersonPositionResult }> {
    return firstValueFrom(
      this.#http.patch<{ success: boolean; data: MovePersonPositionResult }>(
        `${this.#base}/person-positions/${personPositionId}/move`,
        { toPositionId },
      ),
    );
  }

  /** Update an OE (Organisationseinheit) from the org-chart edit panel. */
  updateUnit(input: UpdateOrganizationUnitInput): Promise<OrganizationUnitDto> {
    const { id, ...body } = input;
    return firstValueFrom(
      this.#http.patch<OrganizationUnitDto>(`${this.#base}/units/${id}`, body),
    );
  }

  /** Update a position (Stelle) from the org-chart edit panel. */
  updatePosition(input: UpdatePositionInput): Promise<PositionDto> {
    const { id, ...body } = input;
    return firstValueFrom(
      this.#http.patch<PositionDto>(`${this.#base}/positions/${id}`, body),
    );
  }

  /** Create a sub-OE under a parent OE. */
  createUnit(input: CreateOrganizationUnitInput): Promise<OrganizationUnitDto> {
    return firstValueFrom(
      this.#http.post<OrganizationUnitDto>(`${this.#base}/units`, input),
    );
  }

  /** Create a position (Stelle) inside an OE. */
  createPosition(input: CreatePositionInput): Promise<PositionDto> {
    return firstValueFrom(
      this.#http.post<PositionDto>(`${this.#base}/positions`, input),
    );
  }

  /** Delete an OE (Organisationseinheit). */
  deleteUnit(id: string): Promise<unknown> {
    return firstValueFrom(this.#http.delete(`${this.#base}/units/${id}`));
  }

  /** Delete a position (Stelle). */
  deletePosition(id: string): Promise<unknown> {
    return firstValueFrom(this.#http.delete(`${this.#base}/positions/${id}`));
  }

  /** Create a station inside an OE. */
  createStation(input: CreateStationInput): Promise<StationDto> {
    return firstValueFrom(
      this.#http.post<StationDto>(`${this.#base}/stations`, input),
    );
  }

  /** Update a station from the org-chart edit panel. */
  updateStation(input: UpdateStationInput): Promise<StationDto> {
    const { id, ...body } = input;
    return firstValueFrom(
      this.#http.patch<StationDto>(`${this.#base}/stations/${id}`, body),
    );
  }

  /** Delete a station. */
  deleteStation(id: string): Promise<unknown> {
    return firstValueFrom(this.#http.delete(`${this.#base}/stations/${id}`));
  }

  /** Remove a person's assignment from a position. */
  deletePersonPosition(id: string): Promise<unknown> {
    return firstValueFrom(
      this.#http.delete(`${this.#base}/person-positions/${id}`),
    );
  }

  /** Assign an existing person to a position. */
  assignPerson(input: AssignPersonPositionInput): Promise<unknown> {
    return firstValueFrom(
      this.#http.post(`${this.#base}/person-positions`, input),
    );
  }

  /** Persons belonging to an organization, for the assign-person picker. */
  listSearchPersons(
    params: { organizationId: string; search: string },
    options?: { signal?: AbortSignal },
  ): Promise<PersonSearchDto[]> {
    return firstValueFromAbortable(
      this.#http
        .get<ApiResponse<PersonSearchDto[]>>(
          `${this.#base}/organizations/${params.organizationId}/persons`,
          { params: { search: params.search } },
        )
        .pipe(
          map(
            (response: ApiResponse<PersonSearchDto[]>) => response.data ?? [],
          ),
        ),
      options?.signal,
    );
  }

  /** All roles, for the position role editor. */
  listRoles(options?: { signal?: AbortSignal }): Promise<OrgRoleOption[]> {
    return firstValueFromAbortable(
      this.#http.get<OrgRoleOption[]>('/api/v1/roles'),
      options?.signal,
    );
  }

  /** Attach a role to a position. */
  addPositionRole(positionId: string, roleId: string): Promise<unknown> {
    return firstValueFrom(
      this.#http.post(`${this.#base}/positions/${positionId}/roles`, {
        roleId,
      }),
    );
  }

  /** Detach a role from a position. */
  removePositionRole(positionId: string, roleId: string): Promise<unknown> {
    return firstValueFrom(
      this.#http.delete(`${this.#base}/positions/${positionId}/roles`, {
        body: { roleId },
      }),
    );
  }

  /** Get a role with its scoped permissions. */
  getRoleWithPermissions(
    roleId: string,
    options?: { signal?: AbortSignal },
  ): Promise<RoleWithScopedPermissionsDto> {
    return firstValueFromAbortable(
      this.#http.get<RoleWithScopedPermissionsDto>(
        `/api/v1/roles/${roleId}/permissions`,
      ),
      options?.signal,
    );
  }

  /** Get the scoped entities catalog for the permission matrix. */
  getScopedEntities(options?: {
    signal?: AbortSignal;
  }): Promise<IScopedEntity[]> {
    return firstValueFromAbortable(
      this.#http.get<IScopedEntity[]>('/api/v1/roles/catalog/entities'),
      options?.signal,
    );
  }
}
